import {useMemo} from 'react';
import {assign, isEqual, stripHtml, useUpdateEffect, useUpdate} from 'helpers';

interface Buffer<V> {
  before: string;
  focused: boolean;
  parsed: V;
  value: string;
  valueBeforeFocus: string;
}

interface Props<V> {
  onChange: (b: Buffer<V>) => void;
  parse?: (arg: {value: string}) => V;
  stringify?: (value: V) => string;
  value: V;
}

// While the field is focused store all changes in buffer
export function useBuffer<V>(props: Props<V>) {
  const {
    value,
    onChange,
    parse = ({value}: {value: string}) => value as V,
    stringify = (value: V) => value as string
  } = props;

  const update = useUpdate();

  const buffer = useMemo(() => ({
    before: '',
    focused: false,
    parsed: value,
    update: (values: Partial<Buffer<V>>) => assign(buffer, values) && update(),
    value: stringify(value),
    valueBeforeFocus: ''
  }), []);

  const onFocusChange = ({type}: Event) => {
    if (type === 'focus') {
      buffer.update({
        focused: true,
        valueBeforeFocus: buffer.value
      });
    } else if (type === 'blur') {
      buffer.update({
        focused: false,
        parsed: parse(buffer),
        value: stringify(parse(buffer))
      });
      onChange(buffer);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const {currentTarget} = event;
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      currentTarget.blur();
    } else if (event.key === 'Escape') {
      event.stopPropagation();
      buffer.update({value: buffer.valueBeforeFocus || ''});
      requestAnimationFrame(() => currentTarget.blur());
    }
  };

  // if value was changed outside - update buffer
  useUpdateEffect(() => {
    if (!isEqual(value, parse(buffer))) {
      buffer.update({value: stringify(value)});
    }
  }, [value]);

  return [
    {
      value: buffer.value || '',
      onBlur: onFocusChange,
      onFocus: onFocusChange,
      onKeyDown,
      onChange: ({target, nativeEvent: {inputType}}: React.ChangeEvent<HTMLInputElement>) => buffer.update({
        before: buffer.value,
        value: inputType === 'insertFromPaste' ? stripHtml(target.value).result : target.value
      })
    },
    {
      parse,
      focused: buffer.focused,
      update: buffer.update
    }
  ];
}
